<template>
    <div>
        <v-container fluid class="pt-0">
            <v-card class="pb-2 mb-1">
                <v-card-title>{{ $t("SwissLLMSettings") }}
                    <div class="ml-auto">
                            <v-btn
                                @click="cancel()"
                                class="mr-4 mt-2"
                                color="secundary"
                            >
                                {{ $t("Cancel") }}
                            </v-btn>
                            <v-btn
                                @click="saveSettings()"
                                color="primary"
                                class="mt-2"
                            >
                                <v-icon left>mdi-content-save</v-icon>
                                {{ $t("Save") }}
                            </v-btn>
                            </div>
                </v-card-title>
                <v-card-text class="pb-2">
                    <v-row justify="center" align="center" v-if="loading">
                        <div class="text-center">
                            <div>
                                <v-progress-circular class="my-4" :size="50" width="5" color="primary"
                                    indeterminate></v-progress-circular>
                            </div>
                            <p class="display-1">{{ $t("Loading") }}...</p>
                        </div>
                    </v-row>
                    <v-row v-if="!loading">
                        <v-col cols="1"></v-col>
                        <v-col cols="2" class="pb-0">
                            <p class="ma-0 pb-0">{{ $t("UseOnPrompt") }}</p>
                        </v-col>
                        <v-col cols="3" class="pb-0">
                            <p class="ma-0 pb-0">{{ $t("AdditionalNotes") }}</p>
                        </v-col>
                        <v-col cols="5">

                        </v-col>
                    </v-row>
                    <v-row v-if="!loading" class="mb-0 pb-0">
                        <v-col>
                            <div v-for="(item, i) in contentTypeFields" :key="i">
                                <content-type-fields-infomaniak-component :field="item" @addToPrompt="addToTextArea"
                                    @removeFromPrompt="removeFromTextArea"
                                    @addAdditionalNotes="addAdditionalNotesToTextArea"
                                    :refresh="refresh"></content-type-fields-infomaniak-component>
                            </div>
                        </v-col>
                        <v-col>
                        <v-card class="mb-1 elevation-2 mt-0">
                            <v-card-title class="subtitle-2 font-weight-medium grey--text text--darken-1">
                            <v-icon left color="grey darken-0">mdi-text-box-outline</v-icon>
                            {{ $t("Site.AI.Prompt") }}
                            <v-spacer></v-spacer>
                            <v-btn
                                color="primary"
                                icon
                                @click="refreshData"
                                class="ml-1"
                            >
                                <v-icon>mdi-refresh</v-icon>
                            </v-btn>
                            </v-card-title>

                            <v-card-text class="pb-0">
                            <v-textarea
                                v-model="prompt"
                                rows="17"
                                filled
                                hide-details="auto"
                                :error-messages="promptErrors"
                                @blur="$v.prompt.$touch()"
                                :counter="8000"
                                @input="manualChange = true"
                                background-color="grey lighten-5"
                            ></v-textarea>
                            </v-card-text>
                        </v-card>

                        <v-card class="elevation-2 mt-0">
                            <v-card-text class="pb-0">
                                <div class="subtitle-2 font-weight-medium grey--text text--darken-0 mb-4 d-flex align-center">
                                    {{ $t("Settings") }}
                                </div>

                                <v-row align="center" no-gutters class="mb-5">
                                    <v-col cols="12" sm="6" md="6" class="pr-3">
                                        <div class="d-flex align-center">
                                            <v-tooltip top>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-icon
                                                        v-bind="attrs"
                                                        v-on="on"
                                                        color="grey darken-1"
                                                        class="mr-2 ml-1"
                                                    >
                                                        mdi-information-outline
                                                    </v-icon>
                                                </template>
                                                <span>{{ $t("Site.AI.FuzzyThresholdDescription") }}</span>
                                            </v-tooltip>
                                            <v-text-field
                                                :label="$t('Site.AI.FuzzyThreshold')"
                                                v-model="fuzzyThreshold"
                                                type="number"
                                                min="0"
                                                max="100"
                                                dense
                                                hide-details="auto"
                                                :error-messages="fuzzyThresholdErrors"
                                            ></v-text-field>
                                        </div>
                                    </v-col>

                                    <v-col cols="12" sm="6" md="6" class="pl-3">
                                        <llm-model-selector
                                            :value="selectedModel"
                                            @model-changed="onModelChanged"
                                        />
                                    </v-col>
                                </v-row>

                                <v-divider class="mb-4 mt-4"></v-divider>

                                <div class="subtitle-2 font-weight-medium grey--text text--darken-0 mb-1 d-flex align-center">
                                    {{ $t("TestRecognition") }}
                                </div>

                                <v-row align="center">
                                    <v-col cols="12" md="6">
                                        <v-file-input
                                            v-model="selectedFile"
                                            truncate-length="15"
                                            dense
                                            hide-details="auto"
                                        ></v-file-input>
                                    </v-col>
                                    <v-col cols="12" md="6" class="d-flex">
                                        <v-btn
                                            color="primary"
                                            :loading="loadingModal"
                                            @click="performTest()"
                                            class="mr-2"
                                            small
                                        >
                                            <v-icon left>mdi-check</v-icon>
                                            {{ $t("TestRecognition") }}
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

        </v-container>
        <infomaniak-results-modal :loading="loadingModal" :ref="modalKey" :results="performValues"></infomaniak-results-modal>
    </div>
</template>

<script>
import ContentTypeService from '@/services/ContentTypeService';
import ContentTypeFieldsInfomaniakComponent from '@/components/administration/contentType/documentIntelligence/infomaniak/ContentTypeFieldsInfomaniakComponent.vue';
import DocumentIntelligenceProvidersService from '@/services/DocumentIntelligenceProvidersService.js';
import Utils from '@/ecm/common/Utils';
import DocumentIntelligenceProvidersSettings from '@/ecm/models/contentType/documentIntelligence/DocumentIntelligenceProvidersSettings.js';
import InfomaniakResultsModal from './InfomaniakResultsModal.vue';
import { maxLength } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import LlmModelSelector from './LlmModelSelector.vue';
import ParameterService from '@/services/ParameterService';
import ParametersKeys from '@/ecm/variables/ParametersKeys.js';

export default {
    components: {
        ContentTypeFieldsInfomaniakComponent,
        InfomaniakResultsModal,
        LlmModelSelector
    },
    async created() {
        this.loading = true;
        this.documentIntelligenceProviderSettings = this.documentIntelligenceProviderSettings.toDto();
        this.loadContentTypeConfig(this.contentTypeId);
    },
    data() {
        return {
            loading: false,
            contentTypeId: this.$route.params.contentTypeID,
            settingID: this.$route.params.settingID,
            contentTypeFields: [],
            documentIntelligenceProviderSettings: new DocumentIntelligenceProvidersSettings(),
            fieldsCodes: [],
            fieldsToPrompt: [],
            prompt: '',
            jsonFormat: [],
            refresh: false,
            manualChange: false,
            performValues: [],
            showResultsModal: false,
            modalKey: 'modalKey',
            selectedFile: null,
            loadingModal: false,
            selectedModel: '',
            fuzzyThreshold: 75
        };
    },
    mixins: [validationMixin],
    validations: {
        prompt: {
            maxLength: maxLength(8000)
        },
        fuzzyThreshold: {
            min: value => value >= 0,
            max: value => value <= 100
        }
    },
    computed: {
        modalInstance() {
            return this.$refs[this.modalKey];
        },
        promptErrors() {
            const errors = [];
            if (!this.$v.prompt.$dirty) return errors;
            !this.$v.prompt.maxLength && errors.push(this.$t('FieldMaxLenght'));
            return errors;
        },
        fuzzyThresholdErrors() {
            const errors = [];
            if (!this.$v.fuzzyThreshold.$dirty) return errors;
            (!this.$v.fuzzyThreshold.min || !this.$v.fuzzyThreshold.max) && errors.push(this.$t('Site.AI.FuzzyThresholdRange'));
            return errors;
        }
    },
    methods: {
        loadContentTypeConfig(contentTypeId) {
            ContentTypeService.getFields(contentTypeId).then((data) => {
                if (this.settingID != 0) {
                    this.getSettingsById(this.settingID, data);
                } else {
                    for (var item of data) {
                        item.Selected = false;
                        item.additionalNotes = '';
                    }
                    this.contentTypeFields = data;
                    this.loadPrompt();
                    this.loading = false;
                }
                if (!this.selectedModel) {
                    ParameterService.getStringParameter(ParametersKeys.AIDefaultModel).then((param) => {
                        this.selectedModel = param.value;
                    });
                }
            });
        },
        loadPrompt() {
            const fieldsToPromptString = this.fieldsToPrompt.map(item => {
                if (item.additionalNotes && item.additionalNotes.trim().length > 0) {
                    return item.prompt + ', ' + item.additionalNotes;
                }
                return item.prompt;
            }).join('.\n').concat(this.fieldsToPrompt.length ? '.' : '');

            const jsonFormatString = this.jsonFormat.join(';').concat(this.jsonFormat.length ? ';' : '');
            this.prompt = this.$t('InfomaniakPrompt').replace('-*.MARKER_CT_FIELDS.*-', fieldsToPromptString).replace('-*.MARKER_JSON_KEYS.*-', jsonFormatString);
        },

        async getSettingsById(settingID, contentTypeFields) {
            DocumentIntelligenceProvidersService.getDocumentIntelligenceSettings(settingID)
                .then((data) => {
                    this.documentIntelligenceProviderSettings = data;
                    const jsonConfig = JSON.parse(this.documentIntelligenceProviderSettings.configJSON);
                    for (var field of contentTypeFields) {
                        const fieldInConfig = jsonConfig.fieldsConfig.find(x => x.code === field.Code);
                        if (fieldInConfig) {
                            field.Selected = true;
                            field.additionalNotes = fieldInConfig.additionalNotes;
                            this.fieldsCodes.push(field.Code);
                            var promptMapped = this.$t('InfomaniakPromptField').replace('{0}', field.Name).replace('{1}', field.Type).replace('{2}', field.Code);
                            this.fieldsToPrompt.push({ prompt: promptMapped, additionalNotes: fieldInConfig.additionalNotes });
                            this.jsonFormat.push(`'${field.Code}'`);
                        }
                    }
                    this.manualChange = data.isManualPrompt;
                    this.selectedModel = data.llmModel;
                    this.fuzzyThreshold = data.fuzzyThreshold;
                    if (this.manualChange) {
                        this.prompt = this.documentIntelligenceProviderSettings.prompt;
                    } else {
                        this.loadPrompt();
                    }
                    this.contentTypeFields = contentTypeFields;
                    this.loading = false;
                });
        },

        async addToTextArea(prompt, fieldCode) {
            if (this.manualChange) {
                var res = await this.$confirm(this.$t('ManualActionDetected'), {
                    title: this.$t('Confirm'),
                    icon: 'mdi-alert',
                    color: 'warning',
                    buttonTrueText: this.$t('Ok'),
                    buttonFalseText: this.$t('Cancel'),
                });
                if (res == 1) {
                    this.addToPrompt(prompt, fieldCode);
                    this.manualChange = false;
                } else {
                    return;
                }
            } else {
                this.addToPrompt(prompt, fieldCode);
            }
        },
        async removeFromTextArea(event, fieldCode) {
            if (this.manualChange) {
                var res = await this.$confirm(this.$t('ManualActionDetected'), {
                    title: this.$t('Confirm'),
                    icon: 'mdi-alert',
                    color: 'warning',
                    buttonTrueText: this.$t('Ok'),
                    buttonFalseText: this.$t('Cancel'),
                });
                if (res == 1) {
                    this.removeFromPrompt(event, fieldCode);
                    this.manualChange = false;
                } else {
                    return;
                }
            } else {
                this.removeFromPrompt(event, fieldCode);
            }
        },
        async addAdditionalNotesToTextArea(event, additionalNotes) {
            if (this.manualChange) {
                var res = await this.$confirm(this.$t('ManualActionDetected'), {
                    title: this.$t('Confirm'),
                    icon: 'mdi-alert',
                    color: 'warning',
                    buttonTrueText: this.$t('Ok'),
                    buttonFalseText: this.$t('Cancel'),
                });
                if (res == 1) {
                    this.addAdditionalNotes(event, additionalNotes);
                    this.manualChange = false;
                } else {
                    return;
                }
            } else {
                this.addAdditionalNotes(event, additionalNotes);
            }
        },
        addToPrompt(prompt, fieldCode) {
            this.refresh = false;
            this.fieldsToPrompt.push({ prompt: prompt, additionalNotes: '' });
            this.jsonFormat.push(`'${fieldCode}'`);
            this.fieldsCodes.push(fieldCode);
            this.loadPrompt();
        },
        removeFromPrompt(event, fieldCode) {
            const index = this.fieldsToPrompt.findIndex(linea => linea.prompt.includes(event));
            const indexJson = this.jsonFormat.findIndex(linea => linea.includes(fieldCode));
            const indexFiledsCodes = this.fieldsCodes.findIndex(field => field === fieldCode);
            if (index !== -1) {
                this.fieldsToPrompt.splice(index, 1);
            }
            if (indexJson !== -1) {
                this.jsonFormat.splice(indexJson, 1);
            }
            if (indexFiledsCodes !== -1) {
                this.fieldsCodes.splice(indexFiledsCodes, 1);
            }
            this.loadPrompt();
        },
        addAdditionalNotes(event, additionalNotes) {
            const index = this.fieldsToPrompt.findIndex(linea => linea.prompt.includes(event));
            if (index !== -1) {
                if (additionalNotes.trim() === '') {
                    this.fieldsToPrompt[index].additionalNotes = '';
                } else {
                    this.fieldsToPrompt[index].additionalNotes = additionalNotes.toLowerCase();
                }
            }
            this.loadPrompt();
        },
        async refreshData() {
            var res = await this.$confirm(this.$t('RefreshData'), {
                title: this.$t('Confirm'),
                icon: 'mdi-alert',
                color: 'warning',
                buttonTrueText: this.$t('Ok'),
                buttonFalseText: this.$t('Cancel'),
            });
            if (res == 1) {
                this.fieldsToPrompt = [];
                this.jsonFormat = [];
                this.loadPrompt();
                this.refresh = true;
                this.manualChange = false;
            } else {
                return;
            }

        },
        saveSettings() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }
            this.loading = true;
            var fieldsConfig = [];
            for (var field of this.contentTypeFields) {
                if (!field.Selected) continue;
                fieldsConfig.push({
                    code: field.Code,
                    additionalNotes: field.additionalNotes
                });
            }

            var jsonString = JSON.stringify({
                fieldsConfig: fieldsConfig,
                language: 'en',
            });

            this.documentIntelligenceProviderSettings.Id = parseInt(this.settingID);
            this.documentIntelligenceProviderSettings.ContentTypeID = parseInt(this.contentTypeId);
            this.documentIntelligenceProviderSettings.DocumentIntelligenceProviderID = 3;
            this.documentIntelligenceProviderSettings.Prompt = this.prompt;
            this.documentIntelligenceProviderSettings.ConfigJSON = jsonString;
            this.documentIntelligenceProviderSettings.IsManualPrompt = this.manualChange;
            this.documentIntelligenceProviderSettings.LLMModel = this.selectedModel;
            this.documentIntelligenceProviderSettings.FuzzyThreshold = this.fuzzyThreshold;
            DocumentIntelligenceProvidersService.SaveContentTypeDocumentIntelligenceProviderAsync(this.documentIntelligenceProviderSettings)
                .then((data) => {
                    Utils.toast.success(this.$t('DocumentIntelligenceProviderSettings') + ' ' + this.$t('OperationSuccess'));
                    this.$router.push(`/administration/content-type/document-intelligence-settings/${this.contentTypeId}`);
                })
                .catch(() => {
                    this.loading = false;
                });
        },
        cancel() {
            this.$router.push({ path: `/administration/content-type/document-intelligence-settings/${this.contentTypeId}` });
        },
        hideModal() {
            this.showResultsModal = false;
        },
        async performTest() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }
            if (!this.fieldsCodes || this.fieldsCodes.length === 0) {
                Utils.toast.error(this.$t('FieldsRequired'));
                return;
            }

            if (this.selectedFile) {
                this.cleanResults();
                this.loadingModal = true;

                const performTestRequest = {
                    prompt: this.prompt,
                    Fields: this.contentTypeFields.filter((x) => x.Selected),
                    Base64File: await this.getBase64UploadedFile(),
                    LLMModel: this.selectedModel,
                    ContentTypeId: this.contentTypeId,
                    FuzzyThreshold: this.fuzzyThreshold
                };

                DocumentIntelligenceProvidersService.performInfomaniakLLMTest(performTestRequest)
                    .then((data) => {
                        this.performValues = data;
                        this.loadingModal = false;
                        this.modalInstance.showModal();
                    })
                    .catch(() => {
                        this.loadingModal = false;
                    });

            } else {
                Utils.toast.error(this.$t('DocumentAttachmentRequired'));
                return;
            }
        },
        async getBase64UploadedFile() {
            const base64 = Utils.file.toBase64(this.selectedFile);
            return base64;
        },
        cleanResults() {
            this.performValues = null;
        },
        onModelChanged(modelName) {
            this.selectedModel = modelName;
        }
    },
};

</script>
